/**
 * Utility functions for determining and formatting request types in the Review Table
 */

import type { HoursValues } from './balanceUtils'

export interface RequestTypeInfo {
  type: string
  displayType: string
}

export interface TimeFormatInfo {
  display: string
  isFullDay: boolean
}

/**
 * Determines the request type(s) to display based on user input
 *
 * Rules:
 * - If only Vacation hours entered → "Vacation (PTO/ETO)"
 * - If only Float hours entered → "Float"
 * - If only Flexible Personal Days hours entered → "Flexible Personal Days"
 * - If only Other hours entered → show the specific type (e.g., "Jury Duty", "Training/Meetings")
 * - If Birthday is selected → "Birthday"
 * - If multiple types entered → "Multiple (Combined)"
 */
export const determineRequestType = (
  hoursValues: HoursValues,
  leaveTypeSelected: Record<string, string>,
  isBirthdayChecked: boolean
): RequestTypeInfo => {
  // Parse hours values to numbers
  const vacationHours = Number.parseFloat(hoursValues.vacation || '0') || 0
  const floatHours = Number.parseFloat(hoursValues.float || '0') || 0
  const flexibleHours = Number.parseFloat(hoursValues.flexible || '0') || 0
  const otherHours = Number.parseFloat(hoursValues.other || '0') || 0

  // Collect all non-zero types
  const activeTypes: string[] = []

  if (vacationHours > 0) activeTypes.push('vacation')
  if (floatHours > 0) activeTypes.push('float')
  if (flexibleHours > 0) activeTypes.push('flexible')
  if (otherHours > 0) activeTypes.push('other')
  if (isBirthdayChecked) activeTypes.push('birthday')

  // If no types selected, default to "Vacation (PTO/ETO)"
  if (activeTypes.length === 0) {
    return {
      type: 'vacation',
      displayType: 'Vacation (PTO/ETO)',
    }
  }

  // If only one type selected, return that type's display name
  if (activeTypes.length === 1) {
    const singleType = activeTypes[0]
    return getSingleTypeDisplay(singleType, leaveTypeSelected[singleType])
  }

  // Multiple types → "Multiple (Combined)"
  return {
    type: 'multiple',
    displayType: 'Multiple (Combined)',
  }
}

/**
 * Returns display name for a single request type
 */
const getSingleTypeDisplay = (
  type: string,
  subType?: string
): RequestTypeInfo => {
  const displayMap: Record<string, string> = {
    vacation: 'Vacation (PTO/ETO)',
    float: 'Float',
    flexible: 'Flexible Personal Days',
    birthday: 'Birthday',
  }

  if (displayMap[type]) {
    return {
      type,
      displayType: displayMap[type],
    }
  }

  // For "other" type, show the specific subtype
  if (type === 'other' && subType) {
    return {
      type: 'other',
      displayType: getOtherTypeDisplay(subType),
    }
  }

  // Fallback for "other" without subtype
  return {
    type: 'other',
    displayType: 'Other',
  }
}

/**
 * Maps other leave type IDs to their display names with "Other - " prefix
 */
export const getOtherTypeDisplay = (typeId: string): string => {
  const otherTypeMap: Record<string, string> = {
    OTH_JD: 'Other - Jury Duty',
    OTH_UB: 'Other - Union Business',
    OTH_BL: 'Other - Bereavement Leave',
    OTH_TM: 'Other - Training/Meetings',
    OTH_ET: 'Other - Education Time',
    OTH_DA: 'Other - Doctors Appointment',
    OTH_UT: 'Other - Unpaid Time',
    OTH_LT: 'Other - Labor Management Partnership Time',
  }

  return otherTypeMap[typeId] || 'Other'
}

/**
 * Formats time for display in the review table
 *
 * Rules:
 * - If duration is "Full Day" → return "FullDay"
 * - If duration is "Partial Day" → return formatted time range (e.g., "8:00 AM - 5:00 PM")
 */
export const formatTimeDisplay = (
  duration: string,
  fromTime?: any,
  toTime?: any
): TimeFormatInfo => {
  if (duration === 'Full Day') {
    return {
      display: 'FullDay',
      isFullDay: true,
    }
  }

  // Partial Day - format times
  if (duration === 'Partial Day' && fromTime && toTime) {
    const fromDisplay = formatSingleTime(fromTime)
    const toDisplay = formatSingleTime(toTime)
    return {
      display: `${fromDisplay} - ${toDisplay}`,
      isFullDay: false,
    }
  }

  // Fallback
  return {
    display: '8:00 AM - 5:00 PM',
    isFullDay: false,
  }
}

/**
 * Formats a single time (hour:minute AM/PM)
 */
const formatSingleTime = (time: any): string => {
  // Extract value from DropdownOption or use direct string
  const hour =
    typeof time.hour === 'string' ? time.hour : time.hour?.value || '1'
  const minute =
    typeof time.minute === 'string' ? time.minute : time.minute?.value || '00'
  const ampm =
    typeof time.ampm === 'string' ? time.ampm : time.ampm?.value || 'AM'
  return `${hour}:${minute} ${ampm}`
}
